/*
 * ================================================================
 * MARKETPLACE : LIVRAISON + PAIEMENT (libellés d'affichage)
 * ================================================================
 *
 * Source unique des LIBELLÉS côté frontend. Les VALEURS disponibles
 * (méthodes, opérateurs, options de livraison, pays) viennent toujours du
 * backend :
 *   GET /marketplace/payment/options
 *   GET /marketplace/inquiry/{inquiryId}/delivery/options
 *
 * Aucun provider technique (ElyonPay, Stripe...) n'est choisi ici :
 * l'orchestrateur de paiement le résout côté serveur.
 * ================================================================
 */

import { formatMoney } from "../utils/money";

export const DELIVERY_TYPE_ORDER = [
    "DELIVERY_BY_BUYER",
    "DELIVERY_BY_PLATFORM",
    "DELIVERY_BY_EXTERNAL"
];

export const DELIVERY_TYPE_LABELS = {
    DELIVERY_BY_BUYER: {
        title: "Retrait au siège ASSIGANTO",
        description: "Vous récupérerez votre commande au siège/agence ASSIGANTO. Vous pouvez payer sur place ou payer en ligne puis vous présenter avec votre bordereau de paiement."
    },
    DELIVERY_BY_PLATFORM: {
        title: "Livraison par ASSIGANTO",
        description: "ASSIGANTO prend en charge la livraison à l'adresse de votre choix."
    },
    DELIVERY_BY_EXTERNAL: {
        title: "Livraison par un partenaire",
        description: "Un partenaire de livraison indépendant livre votre commande. Comparez les offres disponibles."
    }
};

export const PAYMENT_METHOD_LABELS = {
    MOBILE_MONEY: {
        title: "Mobile Money",
        description: "Payez depuis votre compte Mobile Money. Vous confirmerez sur votre téléphone."
    },
    CARD: {
        title: "Carte bancaire",
        description: "Vous serez redirigé vers une page de paiement sécurisée. Vos données de carte ne transitent pas par ASSIGANTO."
    },
    PAYPAL: {
        title: "PayPal",
        description: "Vous serez redirigé vers PayPal pour valider le paiement."
    },
    CASH: {
        title: "Espèces à l'agence",
        description: "Vous payez en espèces au siège ASSIGANTO lors du retrait de votre commande."
    }
};

export const MOBILE_OPERATOR_LABELS = {
    T_MONEY: "TMoney",
    MOOV_MONEY: "Moov Money",
    MTN_MONEY: "MTN Mobile Money",
    ORANGE_MONEY: "Orange Money",
    WAVE_MONEY: "Wave"
};

export const UNAVAILABLE_REASON_LABELS = {
    PAYMENT_ROUTE_UNAVAILABLE: "Aucun prestataire de paiement pour ce moyen dans votre pays et votre devise",
    MOBILE_MONEY_UNAVAILABLE: "Mobile Money pas encore disponible dans votre pays",
    NOT_OFFERED_IN_COUNTRY: "Non proposé dans votre pays",
    PAYMENT_SERVICE_UNAVAILABLE: "Service de paiement inaccessible",
    PLATFORM_ZONE_NOT_SERVED: "ASSIGANTO ne livre pas encore à cette adresse",
    PLATFORM_DELIVERY_UNAVAILABLE: "Le service de livraison ASSIGANTO est momentanément indisponible",
    NO_PARTNER_AVAILABLE: "Aucun partenaire de livraison disponible pour cette adresse",
    EXTERNAL_DELIVERY_UNAVAILABLE: "Le service des partenaires de livraison est momentanément indisponible",
    DELIVERY_CURRENCY_MISMATCH: "Tarif de livraison indisponible dans la devise de l'accord"
};

export const PAYMENT_STATUS_LABELS = {
    CREATED: { label: "Paiement en préparation", color: "processing" },
    PENDING: { label: "Paiement en attente", color: "processing" },
    PROCESSING: { label: "Paiement en cours de traitement", color: "processing" },
    SUCCEEDED: { label: "Payé", color: "success" },
    FAILED: { label: "Paiement échoué", color: "error" },
    CANCELLED: { label: "Paiement annulé", color: "default" },
    EXPIRED: { label: "Paiement expiré", color: "default" },
    REFUNDED: { label: "Remboursé", color: "purple" }
};

export const AGREEMENT_STATUS_LABELS = {
    CREATED: { label: "À valider", color: "blue" },
    PAYMENT_PENDING: { label: "Paiement en attente", color: "gold" },
    PAID: { label: "Payé", color: "green" },
    ACCEPTED: { label: "Accepté", color: "blue" },
    REJECTED: { label: "Refusé", color: "red" },
    EXPIRED: { label: "Expiré", color: "default" },
    CANCELLED: { label: "Annulé", color: "default" }
};

export const DELIVERY_STATUS_LABELS = {
    CREATED: { label: "Colis enregistré", color: "blue" },
    READY_FOR_PICKUP: { label: "Prêt", color: "cyan" },
    PICKED_UP: { label: "Pris en charge", color: "blue" },
    IN_TRANSIT: { label: "En transit", color: "processing" },
    OUT_FOR_DELIVERY: { label: "En cours de livraison", color: "gold" },
    DELIVERED: { label: "Livré", color: "success" },
    CANCELLED: { label: "Annulé", color: "default" }
};

export const SERVICE_TYPE_LABELS = {
    STANDARD: "Standard",
    EXPRESS: "Express",
    ECONOMY: "Économique",
    INTERNATIONAL: "International"
};

/**
 * Libellés des pays de livraison. Les valeurs sont celles de l'enum Country
 * du backend (mêmes codes que le catalogue de paiement).
 */
export const COUNTRY_LABELS = {
    TOGO: "Togo",
    BENIN: "Bénin",
    COTE_DIVOIRE: "Côte d'Ivoire",
    SENEGAL: "Sénégal",
    CAMEROUN: "Cameroun",
    GHANA: "Ghana",
    NIGERIA: "Nigeria",
    MALI: "Mali",
    BURKINA_FASO: "Burkina Faso",
    NIGER: "Niger"
};

/** Paiement toujours en cours (suivi par polling). */
export const ACTIVE_PAYMENT_STATUSES = ["CREATED", "PENDING", "PROCESSING"];

export const ORDER_STATUS_LABELS = {
    CREATED: { label: "Commande créée", color: "blue" },
    CONFIRMED: { label: "Commande confirmée", color: "green" },
    PREPARING: { label: "En préparation", color: "gold" },
    READY_FOR_PICKUP: { label: "Prête au retrait", color: "cyan" },
    IN_TRANSIT: { label: "En transit", color: "processing" },
    DELIVERED: { label: "Livrée", color: "success" },
    CANCELLED: { label: "Annulée", color: "default" }
};

/** Message affiché quand le service de paiement (orchestrateur) est injoignable. */
export const PAYMENT_SERVICE_UNAVAILABLE_MESSAGE = {
    title: "Paiement temporairement indisponible",
    description: "Le service de paiement n'est pas accessible. Veuillez réessayer ou choisir un autre moyen de paiement."
};

/** Statuts de paiement pour lesquels une nouvelle tentative est possible. */
export const RETRYABLE_PAYMENT_STATUSES = ["FAILED", "CANCELLED", "EXPIRED"];

export const unavailableReasonLabel = reason =>
    UNAVAILABLE_REASON_LABELS[reason] || "Indisponible";

export const formatDate = value => {
    if (!value) {
        return "—";
    }
    try {
        return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(new Date(value));
    } catch {
        return String(value);
    }
};

/** Alias historique de formatMoney (src/utils/money.js). */
export const formatAmount = (amount, currency) => formatMoney(amount, currency);

/** Message d'erreur exploitable : le backend renvoie { error, message }. */
export const apiErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

export const apiErrorCode = error => error?.response?.data?.error || null;

/** Clé d'idempotence d'une tentative de paiement (8 à 100 caractères). */
export const newIdempotencyKey = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return `pay-${crypto.randomUUID()}`;
    }
    return `pay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * Étape post-Agreement d'un accord enrichi par GET /marketplace/purchases.
 * L'Agreement ne change pas de statut au paiement : l'avancement vient du
 * checkout, du dernier paiement et de la commande.
 *
 * @returns {{ key: string, label: string, color: string, action: null | "VALIDATE" | "PAY" | "FOLLOW_PAYMENT" | "TRACK" }}
 */
export const purchaseStage = (agreement, currentUserId) => {
    const isBuyer = Boolean(currentUserId) && String(agreement?.buyerId) === String(currentUserId);
    const isSeller = Boolean(currentUserId) && String(agreement?.sellerId) === String(currentUserId);
    const payable = ["CREATED", "PAYMENT_PENDING"].includes(agreement?.status);

    if (agreement?.orderId) {
        const delivery = DELIVERY_STATUS_LABELS[agreement.deliveryStatus];
        return {
            key: "ORDER",
            label: `Commande n° ${agreement.orderNumber ?? "—"}${delivery ? ` · ${delivery.label}` : ""}`,
            color: delivery?.color || "green",
            action: isBuyer || isSeller ? "TRACK" : null
        };
    }
    if (ACTIVE_PAYMENT_STATUSES.includes(agreement?.latestPaymentStatus)) {
        return {
            key: "PAYMENT_IN_PROGRESS",
            label: PAYMENT_STATUS_LABELS[agreement.latestPaymentStatus]?.label || "Paiement en cours",
            color: "processing",
            action: isBuyer ? "FOLLOW_PAYMENT" : null
        };
    }
    if (RETRYABLE_PAYMENT_STATUSES.includes(agreement?.latestPaymentStatus)) {
        return {
            key: "PAYMENT_FAILED",
            label: isBuyer ? "Paiement échoué — vous pouvez réessayer" : "Paiement de l'acheteur échoué",
            color: "error",
            action: isBuyer && payable ? "PAY" : null
        };
    }
    if (agreement?.checkoutStatus) {
        return {
            key: "CHECKOUT",
            label: isBuyer ? "Prêt à payer" : "En attente du paiement de l'acheteur",
            color: "gold",
            action: isBuyer && payable ? "PAY" : null
        };
    }
    if (!payable) {
        const status = AGREEMENT_STATUS_LABELS[agreement?.status];
        return { key: "CLOSED", label: status?.label || agreement?.status, color: status?.color || "default", action: null };
    }
    return {
        key: "TO_VALIDATE",
        label: isBuyer ? "À valider" : "En attente de l'acheteur",
        color: "blue",
        action: isBuyer ? (agreement.status === "CREATED" ? "VALIDATE" : "PAY") : null
    };
};
